import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  ForwardRefRenderFunction,
} from 'react';
import { Autocomplete, Grid, TextField, Typography } from '@mui/material';

import {
  Option,
  hoursOptions,
  minutesOptions,
  CustomGranularityFilter,
} from '../helper';

interface Props {
  selectedValuesCustom?: CustomGranularityFilter;
}

export interface Ref {
  selectedValues: CustomGranularityFilter;
  handleHideMinutesOption: (hide: boolean) => void;
}

const DataSpecificFilter: ForwardRefRenderFunction<Ref, Props> = (
  { selectedValuesCustom },
  ref
) => {
  const initValues = useRef<CustomGranularityFilter>({
    startTime: {
      hour: 0,
      minute: 0,
    },
    endTime: {
      hour: 23,
      minute: 0,
    },
  });

  const [selectedValues, setSelectedValues] = useState<CustomGranularityFilter>(
    selectedValuesCustom ?? initValues.current
  );
  const [hideMinutesOption, setHideMinutesOption] = useState<boolean>(true);

  const handleHideMinutesOption = (hide: boolean) => {
    setHideMinutesOption(hide);

    initValues.current.startTime.minute = 0;
    initValues.current.endTime.minute = 45;

    if (hide) {
      initValues.current.endTime.minute = 0;
    }
  };

  const getHoursSelectedOption = (selectedOption: string) =>
    hoursOptions.find((el) => el.value === selectedOption);

  const getMinutesSelectedOption = (selectedOption: string) =>
    minutesOptions.find((el) => el.value === selectedOption);

  const handleChange = (
    value: Option | null,
    timeType: 'StartTime' | 'EndTime',
    hoursMinutes: 'Hours' | 'Minutes'
  ) => {
    if (value) {
      const clonedSelectedValues = { ...selectedValues };
      if (timeType === 'StartTime') {
        if (hoursMinutes === 'Hours') {
          clonedSelectedValues.startTime.hour = +value.value;
        } else if (hoursMinutes === 'Minutes') {
          clonedSelectedValues.startTime.minute = +value.value;
        }
      } else if (timeType === 'EndTime') {
        if (hoursMinutes === 'Hours') {
          clonedSelectedValues.endTime.hour = +value.value;
        } else if (hoursMinutes === 'Minutes') {
          clonedSelectedValues.endTime.minute = +value.value;
        }
      }
      setSelectedValues(clonedSelectedValues);
    }
  };

  useImperativeHandle(ref, () => ({
    selectedValues,
    handleHideMinutesOption,
  }));

  useEffect(() => {
    if (selectedValuesCustom) {
      setSelectedValues(selectedValuesCustom);
    }
  }, [selectedValuesCustom]);

  return (
    <div className="granularity-specific-container">
      <Grid container gap={2} sx={{ marginTop: '1.5rem' }}>
        <Grid item xs={3} sx={{ alignSelf: 'center' }}>
          <Typography>Start Time:</Typography>
        </Grid>
        <Grid item xs={4}>
          <Autocomplete
            size="small"
            id="tags-outlined"
            filterSelectedOptions
            options={hoursOptions}
            value={
              selectedValues.startTime.hour !== -1
                ? getHoursSelectedOption(
                    selectedValues.startTime.hour.toString()
                  )
                : undefined
            }
            ListboxProps={{ style: { maxHeight: 150 } }}
            getOptionLabel={(option: any) => option.label}
            renderInput={(params) => <TextField {...params} label="Hours" />}
            onChange={(
              event: React.SyntheticEvent,
              value: string | Option | null,
              reason: any,
              details?: any
            ) => handleChange(value as Option | null, 'StartTime', 'Hours')}
          />
        </Grid>
        {!hideMinutesOption && (
          <Grid item xs={4}>
            <Autocomplete
              size="small"
              id="tags-outlined"
              filterSelectedOptions
              options={minutesOptions}
              ListboxProps={{ style: { maxHeight: 150 } }}
              value={
                selectedValues.startTime.hour !== -1
                  ? getMinutesSelectedOption(
                      selectedValues.startTime.minute.toString()
                    )
                  : undefined
              }
              getOptionLabel={(option: any) => option.label}
              renderInput={(params) => (
                <TextField {...params} label="Minutes" />
              )}
              onChange={(
                event: React.SyntheticEvent,
                value: string | Option | null,
                reason: any,
                details?: any
              ) => handleChange(value as Option | null, 'StartTime', 'Minutes')}
            />
          </Grid>
        )}
      </Grid>
      <Grid container gap={2} sx={{ marginTop: '1.5rem' }}>
        <Grid item xs={3} sx={{ alignSelf: 'center' }}>
          <Typography>End Time:</Typography>
        </Grid>
        <Grid item xs={4}>
          <Autocomplete
            size="small"
            id="tags-outlined"
            filterSelectedOptions
            options={hoursOptions}
            ListboxProps={{ style: { maxHeight: 150 } }}
            getOptionLabel={(option: any) => option.label}
            renderInput={(params) => <TextField {...params} label="Hours" />}
            onChange={(
              event: React.SyntheticEvent,
              value: string | Option | null,
              reason: any,
              details?: any
            ) => handleChange(value as Option | null, 'EndTime', 'Hours')}
            value={
              selectedValues.startTime.hour !== -1
                ? getHoursSelectedOption(selectedValues.endTime.hour.toString())
                : undefined
            }
          />
        </Grid>
        {!hideMinutesOption && (
          <Grid item xs={4}>
            <Autocomplete
              size="small"
              id="tags-outlined"
              filterSelectedOptions
              options={minutesOptions}
              ListboxProps={{ style: { maxHeight: 150 } }}
              getOptionLabel={(option: any) => option.label}
              renderInput={(params) => (
                <TextField {...params} label="Minutes" />
              )}
              onChange={(
                event: React.SyntheticEvent,
                value: string | Option | null,
                reason: any,
                details?: any
              ) => handleChange(value as Option | null, 'EndTime', 'Minutes')}
              value={
                selectedValues.startTime.hour !== -1
                  ? getMinutesSelectedOption(
                      selectedValues.endTime.minute.toString()
                    )
                  : undefined
              }
            />
          </Grid>
        )}
      </Grid>
    </div>
  );
};

export default forwardRef<Ref, Props>(DataSpecificFilter);
